import React, { useMemo } from "react";
import { Box } from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { AiComparisonStatus } from "../Enums.js";
import { deriveTheme } from "../components/aiReview/deriveTheme.js";
import "./AiComparisonCell.css";

const STATUS_META = {
  [AiComparisonStatus.SAME]: {
    className: "same",
    label: "AI agrees",
    shortLabel: "Match",
  },
  [AiComparisonStatus.SUGGESTION]: {
    className: "suggestion",
    label: "AI suggestion",
    shortLabel: "Suggests",
  },
  [AiComparisonStatus.NO_AI_PICK]: {
    className: "no-ai-pick",
    label: "No AI pick",
    shortLabel: "No pick",
  },
  [AiComparisonStatus.AMBIGUOUS]: {
    className: "ambiguous",
    label: "AI unsure",
    shortLabel: "Unsure",
  },
  [AiComparisonStatus.DIFFERENT]: {
    className: "different",
    label: "AI differs",
    shortLabel: "Differs",
  },
};

function joinLabels(items) {
  return (Array.isArray(items) ? items : [])
    .map((item) => item?.label || item?.name || item?.value)
    .filter(Boolean)
    .join(", ");
}

function getComparisonTitle(comparison) {
  const submitterLabel = comparison?.submitterLabel || comparison?.labels?.empty || "No value selected";
  const aiLabel = comparison?.aiLabel || "";
  const aiCandidates = joinLabels(comparison?.aiCandidates);
  const submitterPrefix = comparison?.labels?.submitter || "Submitter";
  const aiPrefix = comparison?.labels?.ai || "AI";

  switch (comparison?.status) {
    case AiComparisonStatus.SAME:
      return `${aiPrefix} agrees: ${submitterLabel}`;
    case AiComparisonStatus.SUGGESTION:
      return `${submitterPrefix}: ${submitterLabel}. ${aiPrefix} suggests: ${aiLabel}`;
    case AiComparisonStatus.AMBIGUOUS:
      if (comparison?.submitterMatchesAnyCandidate) {
        return `${submitterPrefix}: ${submitterLabel}. ${aiPrefix} included this among multiple plausible options: ${aiCandidates}`;
      }
      return aiCandidates
        ? `${aiPrefix} is unsure between: ${aiCandidates}`
        : `${aiPrefix} is unsure`;
    case AiComparisonStatus.DIFFERENT:
      return `${submitterPrefix}: ${submitterLabel}. ${aiPrefix} suggests: ${aiLabel || aiCandidates || "No clear pick"}`;
    case AiComparisonStatus.NO_AI_PICK:
      return `${aiPrefix}: no clear pick`;
    default:
      return "";
  }
}

function renderMarkerContent(meta, comparison) {
  if (comparison?.status === AiComparisonStatus.SAME) {
    return (
      <>
        <span className="ai-comparison-cell__icon-wrap ai-comparison-cell__icon-wrap--lead">
          <AutoAwesomeRoundedIcon className="ai-comparison-cell__icon" />
        </span>
        <span className="ai-comparison-cell__icon-wrap ai-comparison-cell__icon-wrap--mid">
          <PersonRoundedIcon className="ai-comparison-cell__icon" />
        </span>
        <span className="ai-comparison-cell__text">{meta.shortLabel}</span>
        <span className="ai-comparison-cell__icon-wrap ai-comparison-cell__icon-wrap--trail">
          <CheckCircleRoundedIcon className="ai-comparison-cell__icon" />
        </span>
      </>
    );
  }

  if (comparison?.status === AiComparisonStatus.AMBIGUOUS) {
    return (
      <>
        <span className="ai-comparison-cell__icon-wrap ai-comparison-cell__icon-wrap--lead">
          <AutoAwesomeRoundedIcon className="ai-comparison-cell__icon" />
        </span>
        <span className="ai-comparison-cell__text">
          {comparison?.submitterMatchesAnyCandidate ? "AI option" : meta.shortLabel}
        </span>
        <span className="ai-comparison-cell__icon-wrap ai-comparison-cell__icon-wrap--trail">
          <HelpOutlineRoundedIcon className="ai-comparison-cell__icon" />
        </span>
      </>
    );
  }

  if (comparison?.status === AiComparisonStatus.SUGGESTION) {
    return (
      <>
        <span className="ai-comparison-cell__icon-wrap ai-comparison-cell__icon-wrap--lead">
          <AutoAwesomeRoundedIcon className="ai-comparison-cell__icon" />
        </span>
        <span className="ai-comparison-cell__text">{meta.shortLabel}</span>
        <span className="ai-comparison-cell__icon-wrap ai-comparison-cell__icon-wrap--trail">
          <PersonRoundedIcon className="ai-comparison-cell__icon" />
        </span>
      </>
    );
  }

  return (
    <>
      <span className="ai-comparison-cell__icon-wrap ai-comparison-cell__icon-wrap--lead">
        <AutoAwesomeRoundedIcon className="ai-comparison-cell__icon" />
      </span>
      <span className="ai-comparison-cell__text">{meta.shortLabel}</span>
      <span className="ai-comparison-cell__icon-wrap ai-comparison-cell__icon-wrap--trail">
        <RemoveCircleOutlineRoundedIcon className="ai-comparison-cell__icon" />
      </span>
    </>
  );
}

export default function AiComparisonCell({ comparison, children, onOpenComparison, theme }) {
  const T = useMemo(() => deriveTheme(theme), [theme]);

  if (
    !comparison?.enabled ||
    comparison.status === AiComparisonStatus.DISABLED ||
    comparison.status === AiComparisonStatus.NO_AI_PICK
  ) {
    return children;
  }

  const meta = STATUS_META[comparison.status] || STATUS_META[AiComparisonStatus.NO_AI_PICK];
  const tone = comparison.tone || "normal";
  const title = getComparisonTitle(comparison);
  const isAmbiguousMatch =
    comparison?.status === AiComparisonStatus.AMBIGUOUS &&
    comparison?.submitterMatchesAnyCandidate === true;
  const canOpenComparison = typeof onOpenComparison === "function";
  const themeStyle = {
    "--aicc-ai-colour": T.aiColour,
    "--aicc-user-colour": T.userColour,
    "--aicc-match-colour": T.aiMatchColour,
    "--aicc-suggestion-colour": T.aiSuggestionColour,
    "--aicc-ambiguous-colour": T.aiAmbiguousColour,
    "--aicc-no-ai-pick-colour": T.aiNoPickColour,
    "--aicc-neutral-colour": T.aiNeutralColour,
  };

  const handleOpenComparison = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (canOpenComparison) {
      onOpenComparison(event);
    }
  };

  const handleComparisonMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Box
      className={`ai-comparison-cell ai-comparison-cell--${meta.className} ai-comparison-cell--tone-${tone}${isAmbiguousMatch ? " ai-comparison-cell--ambiguous-match" : ""}`}
      title={title}
      aria-label={title || meta.label}
      style={themeStyle}
    >
      {comparison.status === AiComparisonStatus.DIFFERENT ? null : (
        <Box
          component="button"
          type="button"
          className="ai-comparison-cell__marker"
          title={title}
          aria-label={title || meta.label}
          disabled={!canOpenComparison}
          onClick={handleOpenComparison}
          onMouseDown={handleComparisonMouseDown}
        >
          {renderMarkerContent(meta, comparison)}
        </Box>
      )}
      <Box className="ai-comparison-cell__content">
        {children}
      </Box>
      {comparison.status === AiComparisonStatus.DIFFERENT ? (
        <Box
          component="button"
          type="button"
          className="ai-comparison-cell__circuit"
          title={title}
          aria-label={title || "AI differs from user choice"}
          disabled={!canOpenComparison}
          onClick={handleOpenComparison}
          onMouseDown={handleComparisonMouseDown}
        >
          <span className="ai-comparison-cell__mismatch-token ai-comparison-cell__mismatch-token--user">
            <PersonRoundedIcon className="ai-comparison-cell__mismatch-svg" />
          </span>
          <span className="ai-comparison-cell__mismatch-operator">{"\u2260"}</span>
          <span className="ai-comparison-cell__mismatch-token ai-comparison-cell__mismatch-token--ai">
            <AutoAwesomeRoundedIcon className="ai-comparison-cell__mismatch-svg" />
          </span>
        </Box>
      ) : null}
    </Box>
  );
}
